import { Component, computed, Inject, input, LOCALE_ID, OnInit, output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { getLoggerFor, ILogger, LogInvocation, LogLevel } from "@tylertech/logging";
import { ICategoryChartData, IMetadataChart, IPieChartData, ChartType as MetadataChartType } from "@tylertech/tyler-api-types";
import { ChartConfiguration, ChartConfigurationCustomTypesPerDataset } from "chart.js";
import { ValueChangedParms } from "../../interfaces";
import { MetadataEventService } from "../../services/metadataEventService";
import { MetadataChartConverter } from "./chartConverter";

/** simple module-scoped counter to generate instance-unique id for MetadataChartComponent */
let counter = 0;

@Component({
    selector: "metadata-chart",
    standalone: false,
    templateUrl: "./metadataChart.tmpl.html",
    styles: `chartjs-container {
    min-height: 300px;
}`,
})
export class MetadataChartComponent implements OnInit
{
    private readonly log: ILogger;

    readonly model = input<Readonly<IMetadataChart>>();

    readonly valueChanged = output<ValueChangedParms>();

    protected readonly listenerId = `MetadataChartComponent[${counter++}]`;

    readonly hasData = computed(() =>
    {
        const model = this.model();
        return !model
            || (model.Type === MetadataChartType.Pie
                ? !!(model as IPieChartData).Data?.length
                : !!(model as ICategoryChartData).Series?.length);
    });

    chartJsConfig: ChartConfiguration | ChartConfigurationCustomTypesPerDataset;

    constructor(
        @Inject(LOCALE_ID) private readonly locale: string,
        private readonly $metadataEventService: MetadataEventService,
    )
    {
        this.log = getLoggerFor("MetadataChartComponent",
            () => this.model().Title
                ? `MetadataChartComponent("${this.model().Title}")`
                : `MetadataChartComponent("")`);

        this.$metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                if (args.Recalculating)
                {
                    return;
                }

                this.setChartData();
            });
    }

    ngOnInit(): void
    {
        this.setChartData();
    }

    @LogInvocation(LogLevel.DEBUG)
    protected setChartData(): void
    {
        if (!this.hasData())
        {
            return;
        }

        this.chartJsConfig = new MetadataChartConverter(this.model(), this.locale)
            .convertChart();
    }
}

